/**
 * Organisation hierarchy from API payload (level_variant / vertical only).
 * Do not use service_variant for tier or display labels.
 */

import type { Post } from '@/stores/auth-store'

export type OrgHierarchyInstanceShape = {
  level_variant_name?: string | null
  level_variant_vertical_system_code?: string | null
  vertical_name?: string | null
  service_variant?: string | number | null
  service_variant_label?: string | null
  service_variant_name?: string | null
  service_variant_service?: string | null
  service_data?: {
    service_name?: string | null
    variant_unique?: string | number | null
  } | null
  type?: string | number | null
  name?: string | null
  title?: string | null
}

/** Catalog vertical code from instance payload (e.g. ROOT_ORG, TENANT, BRANCH). */
export function getHierarchyTierCodeFromInstance(
  instance: OrgHierarchyInstanceShape | null | undefined,
): string | null {
  const code = String(instance?.level_variant_vertical_system_code ?? '').trim()
  return code.length > 0 ? code : null
}

/** User-facing tier label: level variant name, then vertical name, then generic. */
export function getHierarchyDisplayLabelFromInstance(
  instance: OrgHierarchyInstanceShape | null | undefined,
): string {
  const lv = String(instance?.level_variant_name ?? '').trim()
  if (lv) {
    return lv
  }
  const vn = String(instance?.vertical_name ?? '').trim()
  if (vn) {
    return vn
  }
  const n = String(instance?.name ?? instance?.title ?? '').trim()
  return n || 'Location'
}

/** True when catalog vertical tier is branch (from API `level_variant_vertical_system_code`). */
export function isBranchHierarchyTier(instance: OrgHierarchyInstanceShape | null | undefined): boolean {
  const code = (getHierarchyTierCodeFromInstance(instance) ?? '').toUpperCase()
  return code === 'BRANCH'
}

function normalizeRootOrgCandidate(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toUpperCase()
    .replace(/[\s_]+/g, '-')
}

export function isRootOrgServiceInstance(instance: OrgHierarchyInstanceShape | null | undefined): boolean {
  if (!instance) {
    return false
  }

  const tierCode = normalizeRootOrgCandidate(getHierarchyTierCodeFromInstance(instance))
  if (tierCode === 'ROOT-ORG') {
    return true
  }

  const variantUnique = String(instance.service_data?.variant_unique ?? instance.service_variant ?? '').trim()
  if (variantUnique === '100') {
    return true
  }

  return [
    instance.level_variant_name,
    instance.vertical_name,
    instance.service_variant_label,
    instance.service_variant_name,
    instance.service_variant_service,
    instance.service_data?.service_name,
    instance.type,
  ].some((value) => normalizeRootOrgCandidate(value) === 'ROOT-ORG')
}

/**
 * Admin section label from catalog tier. Returns null when tier is unknown — use service-variant fallback.
 */
export function getHierarchyAdminRoleLabelFromInstance(
  instance: OrgHierarchyInstanceShape | null | undefined,
): string | null {
  const code = (getHierarchyTierCodeFromInstance(instance) ?? '').toUpperCase()
  if (code === 'ROOT_ORG') {
    return 'Root Admin'
  }
  if (code === 'TENANT') {
    return 'Primary role'
  }
  if (code === 'BRANCH') {
    return 'Branch Admin'
  }
  return null
}

/**
 * Branch-operator workspace (/org/branch): true when catalog tier is BRANCH.
 * When API tier is present but not BRANCH, false. No service-variant fallback (branch uses SERVICES / 1400).
 */
export function isBranchOperatorWorkspaceContext(
  instance: OrgHierarchyInstanceShape | null | undefined,
  _fallbackVariantUniqueId?: string | number | null,
  _fallbackVariantLabel?: string | null,
): boolean {
  if (isBranchHierarchyTier(instance)) {
    return true
  }
  if (getHierarchyTierCodeFromInstance(instance)) {
    return false
  }
  return false
}

export function isBranchOperatorWorkspaceContextFromPost(post: Post | null | undefined): boolean {
  if (!post) {
    return false
  }
  return isBranchOperatorWorkspaceContext(
    {
      level_variant_name: post.level_variant_name ?? null,
      level_variant_vertical_system_code: post.level_variant_vertical_system_code ?? null,
    },
    post.service_variant_unique_id,
    post.service_variant_name ?? post.service_variant_type,
  )
}
